import React from "react"
import { type RouteObject, createBrowserRouter } from "react-router-dom"

import { ErrorBoundaryFallback } from "../../../components/ErrorElement.tsx"
import { PermissionGate } from "../../../components/PermissionGate/PermissionGate.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { guideRoutes } from "../../Guide/routes/guide-routes.tsx"
import PortalLayout from "../../Portal/PortalLayout.tsx"
import PortalRoute from "../../Portal/PortalRoute.tsx"
import CustomLayout from "../CustomLayout.tsx"
import PrivateRoute from "../PrivateRoute.tsx"
import RootLayout from "../RootLayout.tsx"
import { aiCenterRoutes } from "./routes/ai-center-routes.tsx"
import { archiveRoutes } from "./routes/archive-routes.tsx"
import { authRoutes } from "./routes/auth-routes.tsx"
import { calendarRoutes } from "./routes/calendar-routes.tsx"
import { communicationRoutes } from "./routes/communication-routes.tsx"
import { contractRoutes } from "./routes/contract-routes.tsx"
import { customerRoutes } from "./routes/customer-routes.tsx"
import { esignRoutes } from "./routes/esign-routes.tsx"
import { leadRoutes } from "./routes/lead-routes.tsx"
import { portalRoutes } from "./routes/portal-routes.tsx"
import { projectRoutes } from "./routes/project-routes.tsx"
import { proposalRoutes } from "./routes/proposal-routes.tsx"
import { settingsRoutes } from "./routes/settings-routes.tsx"
import { subcontractorRoutes } from "./routes/subcontractor-routes.tsx"
import { supplierRoutes } from "./routes/supplier-routes.tsx"
import { templatesRoutes } from "./routes/templates-routes.tsx"

import { lazyPage } from "./lazyPage.tsx"
import DeletionLockGate from "../../AccountDeletionLockPage"
import DashboardRoute from "../../DashboardPage/DashboardRoute"
import GlobalMapAvailabilityGate from "../../GlobalMap/GlobalMapAvailabilityGate.tsx"
import InvoicesAvailabilityGate from "../../Invoices/components/InvoicesAvailabilityGate.tsx"
import NotFoundPage from "../../NotFoundPage"
import ReportsShell from "../../ReportsPage/components/shell/ReportsShell"

const AccountDeletionRequestsPage = lazyPage(
  () => import("../../AccountDeletionRequestsPage")
)
const HomePage = lazyPage(() => import("../../DashboardPage/HomePage"))
const DocumentReviewsPage = lazyPage(() =>
  import("../../DocumentReviewsPage/DocumentReviewsPage.tsx").then((m) => ({
    default: m.DocumentReviewsPage
  }))
)
const DocumentReviewWorkspacePage = lazyPage(() =>
  import("../../DocumentReviewsPage/DocumentReviewWorkspacePage.tsx").then(
    (m) => ({ default: m.DocumentReviewWorkspacePage })
  )
)
const GlobalFilesPage = lazyPage(
  () => import("../../GlobalFiles/GlobalFilesPage.tsx")
)
const GlobalMapPage = lazyPage(
  () => import("../../GlobalMap/GlobalMapPage.tsx")
)
const InvoicesPage = lazyPage(() => import("../../Invoices/InvoicesPage.tsx"))
const ReportDetailPage = lazyPage(
  () => import("../../ReportsPage/ReportDetailPage")
)
const ReportEditPage = lazyPage(
  () => import("../../ReportsPage/components/build/ReportEditPage")
)
const ReportsHomePage = lazyPage(
  () => import("../../ReportsPage/components/shell/ReportsHomePage")
)
const ReportWizardPage = lazyPage(
  () => import("../../ReportsPage/components/wizard/ReportWizardPage")
)
const TeamManagementPage = lazyPage(
  () => import("../../TeamManagementPage/components/TeamManagementPage.tsx")
)
const MemberPerformanceRoute = lazyPage(() =>
  import("../../TeamManagementPage/performance/member/MemberPerformanceRoute.tsx").then(
    (m) => ({ default: m.MemberPerformanceRoute })
  )
)
// Protected routes (require auth)
const protectedRoutes: RouteObject[] = [
  {
    path: RoutesPaths.home,
    element: <HomePage />
  },
  {
    path: RoutesPaths.dashboard,
    element: <DashboardRoute />
  },
  {
    path: RoutesPaths.invoices,
    element: (
      <InvoicesAvailabilityGate>
        <PermissionGate pages={["invoices"]}>
          <InvoicesPage />
        </PermissionGate>
      </InvoicesAvailabilityGate>
    )
  },
  {
    path: RoutesPaths.reports.edit(":id"),
    element: <ReportEditPage />
  },
  {
    path: RoutesPaths.reports.list,
    element: <ReportsShell />,
    children: [
      {
        // The list stays mounted under the New Report modal, so closing it
        // returns to the same folder, search and scroll position.
        element: <ReportsHomePage />,
        children: [
          { index: true, element: null },
          // Optional :id segment: the wizard rewrites its URL to
          // /reports/new/:id after the step-1 POST so a refresh resumes it.
          { path: "new/:id?", element: <ReportWizardPage /> }
        ]
      },
      { path: ":id", element: <ReportDetailPage /> }
    ]
  },
  ...calendarRoutes,
  {
    path: RoutesPaths.map,
    element: (
      <GlobalMapAvailabilityGate>
        <GlobalMapPage />
      </GlobalMapAvailabilityGate>
    )
  },
  ...leadRoutes,
  ...projectRoutes,
  ...archiveRoutes,
  // OTHER PAGES
  ...customerRoutes,
  ...subcontractorRoutes,
  ...supplierRoutes,
  {
    path: RoutesPaths.team_management,
    element: (
      <PermissionGate pages={["team_management"]}>
        <TeamManagementPage />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.team_performance_member(":memberId"),
    element: (
      <PermissionGate pages={["team_management"]}>
        <MemberPerformanceRoute />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.document_reviews.list,
    element: (
      <PermissionGate pages={["document_reviews"]}>
        <DocumentReviewsPage />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.files.list(),
    element: (
      <PermissionGate pages={["files"]}>
        <GlobalFilesPage />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.document_reviews.view(":id"),
    element: (
      <PermissionGate pages={["document_reviews"]}>
        <DocumentReviewWorkspacePage />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.account_deletion_requests,
    element: (
      <PermissionGate
        paths={["team_management.features.team_manage_account_deletion"]}
      >
        <AccountDeletionRequestsPage />
      </PermissionGate>
    )
  },
  ...settingsRoutes,
  ...proposalRoutes,
  ...contractRoutes,
  ...esignRoutes,
  ...communicationRoutes,
  ...templatesRoutes,
  ...guideRoutes,
  ...aiCenterRoutes
]

// Wrap protected routes with PrivateRoute HOC
const wrapWithPrivateRoute = (routes: RouteObject[]) => {
  return routes.map((route) => ({
    ...route,
    element: <PrivateRoute>{route.element}</PrivateRoute>
  }))
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundaryFallback />,
    children: [
      // Public/Auth routes
      ...authRoutes,
      // Customer portal — its own gate (PortalRoute) and chrome
      // (PortalLayout), separate from the staff app below.
      {
        element: (
          <PortalRoute>
            <PortalLayout />
          </PortalRoute>
        ),
        children: portalRoutes
      },
      // Protected routes with CustomLayout
      {
        element: (
          <DeletionLockGate>
            <CustomLayout />
          </DeletionLockGate>
        ),
        children: [
          ...wrapWithPrivateRoute(protectedRoutes),
          {
            path: "*",
            element: <NotFoundPage />
          }
        ]
      }
    ]
  }
])
